package com.schoolconnect.app.repository.eventsnews

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.EventCreateRequest
import com.schoolconnect.app.datasource.network.request.EventUpdateRequest
import com.schoolconnect.app.datasource.network.response.EventResponse
import com.schoolconnect.app.datasource.network.response.TeacherEventStatisticsResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey
import kotlinx.coroutines.CoroutineScope

data class EventsPageResult(val items: List<EventResponse>, val totalCount: Int)

object EventsNewsRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] EventsNewsRepository"
    private val pageCache = RepositoryCache<EventsPageResult>()

    suspend fun loadPage(
        query: String? = null,
        eventId: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        status: String? = null,
        audience: String? = null,
        publishedDate: String? = null,
        publishedDateFrom: String? = null,
        publishedDateTo: String? = null,
        sortBy: String? = null,
        order: String? = null,
        refreshScope: CoroutineScope
    ): Result<EventsPageResult> {
        val safeSize = pageSize.coerceIn(1, 100)
        val offset = pageIndex.coerceAtLeast(0) * safeSize
        val key = tenantCacheKey("events:${query.orEmpty()}:${eventId.orEmpty()}:${status.orEmpty()}:${audience.orEmpty()}:${publishedDate.orEmpty()}:${publishedDateFrom.orEmpty()}:${publishedDateTo.orEmpty()}:$offset:$safeSize:${sortBy.orEmpty()}:${order.orEmpty()}")
        return pageCache.load(refreshScope, TAG, "loadPage", key) {
            KtorApiClient.getEvents(query = query, eventId = eventId, status = status, audience = audience, publishedDate = publishedDate, publishedDateFrom = publishedDateFrom, publishedDateTo = publishedDateTo, limit = safeSize, offset = offset, sortBy = sortBy, order = order)
                .map { rows -> EventsPageResult(rows, rows.serverTotalCountOrNull() ?: rows.size) }
                .onFailure { throwable -> scLog("$TAG loadPage failure pageIndex=$pageIndex", throwable) }
        }
    }

    suspend fun loadStatistics(): Result<TeacherEventStatisticsResponse> =
        KtorApiClient.getTeacherEventStatistics()
            .onFailure { throwable -> scLog("$TAG loadStatistics failure", throwable) }

    suspend fun loadDetail(eventId: String): Result<EventResponse> =
        KtorApiClient.getEvent(eventId)
            .onFailure { throwable -> scLog("$TAG loadDetail failure eventId=${eventId.trim()}", throwable) }

    suspend fun createEvent(request: EventCreateRequest, currentUserId: String? = null): Result<EventResponse> {
        val normalized = request.copy(
            schoolId = request.schoolId.trim(),
            title = request.title.trim(),
            description = request.description?.trim()?.takeIf { it.isNotBlank() },
            eventDate = request.eventDate.trim(),
            audience = request.audience?.trim()?.takeIf { it.isNotBlank() },
            attachmentFileIds = request.attachmentFileIds.map(String::trim).filter(String::isNotBlank)
        )
        if (normalized.schoolId.isBlank() || normalized.title.isBlank() || normalized.eventDate.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId, title, and eventDate are required"))
        }
        return KtorApiClient.createEvent(normalized, currentUserId).onSuccess { pageCache.clear() }
            .onFailure { throwable -> scLog("$TAG createEvent failure schoolId=${normalized.schoolId}", throwable) }
    }

    suspend fun updateEvent(eventId: String, request: EventUpdateRequest, currentUserId: String? = null): Result<EventResponse> {
        val normalizedId = eventId.trim()
        if (normalizedId.isBlank()) return Result.failure(IllegalArgumentException("eventId is required"))
        return KtorApiClient.updateEvent(
            normalizedId,
            request.copy(
                title = request.title?.trim()?.takeIf { it.isNotBlank() },
                description = request.description?.trim()?.takeIf { it.isNotBlank() },
                eventDate = request.eventDate?.trim()?.takeIf { it.isNotBlank() },
                audience = request.audience?.trim()?.takeIf { it.isNotBlank() }
            ),
            currentUserId
        ).onSuccess { pageCache.clear() }.onFailure { throwable -> scLog("$TAG updateEvent failure eventId=$normalizedId", throwable) }
    }

    suspend fun deleteEvent(eventId: String): Result<kotlinx.serialization.json.JsonObject> {
        val normalizedId = eventId.trim()
        if (normalizedId.isBlank()) return Result.failure(IllegalArgumentException("eventId is required"))
        return KtorApiClient.deleteEvent(normalizedId).onSuccess { pageCache.clear() }
            .onFailure { throwable -> scLog("$TAG deleteEvent failure eventId=$normalizedId", throwable) }
    }
}
